/* SkeletonHD limited series: Shopify stock minus this cart only.
 * No reservations, timers, inventory writes, or global fetch overrides.
 * Only tagged-product quantity writes enter the guard/queue. Untagged writes pass directly through.
 * Theme-side quantity protection; checkout enforcement requires a Shopify validation app.
 */
(() => {
  'use strict';
  if (window.SHDSeries) return;
  const LIMIT = 10;
  const REFRESH_MS = 15000;
  const nativeFetch = window.fetch.bind(window);
  const variants = new Map();
  const root = () => window.Shopify?.routes?.root || '/';
  let queue = Promise.resolve();
  let channel;
  try { channel = new BroadcastChannel('shd-series-cart'); } catch (_) {}
  function registerProducts() {
    document.querySelectorAll('script[data-shd-series-product]').forEach(node => {
      try {
        const product = JSON.parse(node.textContent);
        product.variants.forEach(id => variants.set(String(id), String(product.id)));
      } catch (_) { /* A malformed optional marker must not break the storefront. */ }
    });
  }
  async function snapshot(path, variantId, signal) {
    const url = new URL(path, location.origin);
    url.searchParams.set('section_id', 'shd-series-state');
    if (variantId) url.searchParams.set('variant', variantId);
    url.searchParams.set('_shd', String(Date.now()));
    const response = await nativeFetch(url.href, {
      cache: 'no-store', credentials: 'same-origin', signal
    });
    if (!response.ok) throw new Error('Inventory snapshot failed');
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const node = doc.querySelector('script[data-shd-series-state]');
    if (!node) throw new Error('Inventory section missing');
    const state = JSON.parse(node.textContent);
    if (!Array.isArray(state.items)) throw new Error('Invalid inventory snapshot');
    state.items.forEach(item => {
      if (item.limited) variants.set(String(item.id), String(item.product_id));
    });
    return state;
  }
  // Preserve original request bodies. This projection is used only for validation.
  function decodeBody(body) {
    if (typeof body === 'string') {
      try { return JSON.parse(body); }
      catch (_) { body = new URLSearchParams(body); }
    }
    if (body instanceof FormData || body instanceof URLSearchParams) {
      const data = {};
      for (const [key, value] of body.entries()) {
        const update = key.match(/^updates\[([^\]]*)\]$/);
        const item = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
        if (update) {
          data.updates ||= update[1] ? {} : [];
          if (update[1]) data.updates[update[1]] = value;
          else data.updates.push(value);
        } else if (item) {
          data.items ||= [];
          data.items[Number(item[1])] ||= {};
          data.items[Number(item[1])][item[2]] = value;
        } else data[key] = value;
      }
      return data;
    }
    return body || {};
  }
  const quantity = value => {
    const n = Number(value);
    if (!Number.isInteger(n) || n < 0) throw new Error('Invalid quantity');
    return n;
  };
  function exceedsLimit(action, data, items) {
    const rows = items.map(item => ({ ...item, quantity: quantity(item.quantity) }));
    const limitedProducts = new Set(items.filter(i => i.limited).map(i => String(i.product_id)));
    const totals = list => {
      const result = new Map();
      list.forEach(i => result.set(String(i.product_id), (result.get(String(i.product_id)) || 0) + i.quantity));
      return result;
    };
    const before = totals(rows);
    function append(id, count) {
      const productId = variants.get(String(id));
      if (!productId) return;
      limitedProducts.add(productId);
      rows.push({ id, product_id: productId, quantity: count });
    }
    if (action === 'add') {
      (data.items || [data]).filter(Boolean).forEach(i => append(i.id, quantity(i.quantity ?? 1)));
    } else if (action === 'change') {
      const row = data.line != null ? rows[Number(data.line) - 1] :
        rows.find(i => i.key === String(data.id)) || rows.find(i => String(i.id) === String(data.id));
      if (row && data.quantity != null) row.quantity = quantity(data.quantity);
    } else if (action === 'update' && data.updates) {
      if (Array.isArray(data.updates)) {
        data.updates.forEach((count, index) => { if (rows[index]) rows[index].quantity = quantity(count); });
      } else {
        Object.entries(data.updates).forEach(([id, count]) => {
          const row = rows.find(i => i.key === id) || rows.find(i => String(i.id) === id);
          if (row) row.quantity = quantity(count);
          else append(id, quantity(count));
        });
      }
    }
    const after = totals(rows);
    // Existing oversized carts may be reduced, but never increased further.
    return [...limitedProducts].some(id => (after.get(id) || 0) > LIMIT &&
      (after.get(id) || 0) > (before.get(id) || 0));
  }
  function errorResponse(message, status = 422) {
    return new Response(JSON.stringify({ status, message, description: message }), {
      status, headers: { 'Content-Type': 'application/json' }
    });
  }
  function notifyCart() {
    document.dispatchEvent(new CustomEvent('shd:cart-mutated'));
    channel?.postMessage('changed');
  }
  channel?.addEventListener('message', () => document.dispatchEvent(new CustomEvent('shd:cart-mutated')));
  async function performCartRequest(input, options = {}) {
    const url = new URL(input, location.origin);
    const action = url.pathname.match(/\/cart\/(add|change|update)(?:\.js)?$/)?.[1];
    if (!action || String(options.method || 'GET').toUpperCase() === 'GET') return nativeFetch(input, options);
    try {
      registerProducts();
      const data = decodeBody(options.body);
      const isQuantityChange = action === 'change' && data.quantity != null || action === 'update' && data.updates;
      const isLimitedAdd = action === 'add' && (data.items || [data]).filter(Boolean).some(i => variants.has(String(i.id)));
      if (isLimitedAdd || isQuantityChange) {
        const validation = new AbortController();
        const cancel = () => validation.abort();
        if (options.signal?.aborted) validation.abort();
        options.signal?.addEventListener('abort', cancel, { once: true });
        const timeout = setTimeout(cancel, 10000);
        let state;
        try { state = await snapshot(`${root()}cart`, null, validation.signal); }
        finally {
          clearTimeout(timeout);
          options.signal?.removeEventListener('abort', cancel);
        }
        if (exceedsLimit(action, data, state.items)) {
          return errorResponse('Limit 10 per limited-series product, across all sizes. Reduce the quantity or remove an item from your cart.');
        }
      }
      const response = await nativeFetch(input, options);
      // A partially successful Shopify request may return an error; refresh either way.
      notifyCart();
      return response;
    } catch (error) {
      if (error.name === 'AbortError' && options.signal?.aborted) throw error;
      return errorResponse('Could not verify or update your cart. Please refresh your cart before trying again.', 503);
    }
  }
  function needsGuard(input, options = {}) {
    if (String(options.method || 'GET').toUpperCase() === 'GET') return false;
    registerProducts();
    const action = new URL(input, location.origin).pathname.match(/\/cart\/(add|change|update)(?:\.js)?$/)?.[1];
    const data = decodeBody(options.body);
    const tagged = id => variants.has(String(id).split(':')[0]);
    if (action === 'add') return (data.items || [data]).filter(Boolean).some(i => tagged(i.id));
    if (action === 'change') return Number(data.quantity) !== 0 && tagged(data.id);
    if (action === 'update' && data.updates && !Array.isArray(data.updates)) {
      return Object.entries(data.updates).some(([id, count]) => Number(count) !== 0 && tagged(id));
    }
    return false;
  }
  function cartFetch(input, options) {
    // Ordinary products, removals, notes and discounts never wait for this helper.
    if (!needsGuard(input, options)) return nativeFetch(input, options);
    // Prevent two quick clicks from both validating against the same pre-add cart.
    const job = () => navigator.locks?.request ?
      navigator.locks.request('shd-series-cart-write', () => performCartRequest(input, options)) :
      performCartRequest(input, options);
    const result = queue.then(job, job);
    queue = result.catch(() => {});
    return result;
  }
  window.SHDSeries = { cartFetch, version: '2026-10-02-repair' };
  class SeriesCounter extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.refreshSoon = () => {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.refresh(), 100);
      };
      ['shd:cart-mutated', 'cart:change', 'cart:refresh', 'cart:updated'].forEach(event =>
        document.addEventListener(event, this.refreshSoon, options));
      document.addEventListener('variant:change', event => {
        if (event.detail?.formId !== this.dataset.formId) return;
        this.dataset.variantId = event.detail.variant?.id || '';
        this.request?.abort();
        // Do not display the previous size's stock during a new request.
        this.hidden = true;
        this.refreshSoon();
      }, options);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refreshSoon(); }, options);
      window.addEventListener('pageshow', this.refreshSoon, options);
      window.addEventListener('focus', this.refreshSoon, options);
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, REFRESH_MS);
      this.refreshSoon();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.request?.abort();
      clearInterval(this.timer);
      clearTimeout(this.debounce);
    }
    async refresh() {
      if (!this.isConnected || document.hidden || !this.dataset.variantId) return;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 10000);
      try {
        const state = await snapshot(this.dataset.productUrl, this.dataset.variantId, request.signal);
        if (this.request !== request || !this.isConnected) return;
        const product = state.product;
        if (!product?.enabled || !product.variant.tracked || String(product.variant.id) !== this.dataset.variantId) {
          this.hidden = true;
          return;
        }
        const ownQuantity = state.items.filter(i => String(i.id) === String(product.variant.id))
          .reduce((sum, i) => sum + i.quantity, 0);
        const count = Math.max(0, product.variant.stock - ownQuantity);
        this.querySelector('[data-shd-remaining]').textContent = String(count);
        this.querySelectorAll('[data-shd-total]').forEach(node => { node.textContent = String(product.total); });
        this.querySelector('[data-shd-bar]').style.width = `${Math.min(100, count / product.total * 100)}%`;
        const size = this.querySelector('[data-shd-size]');
        if (size) size.textContent = product.variant.title;
        this.querySelector('[data-shd-live-label]').textContent = 'Live Inventory';
        this.removeAttribute('data-stale');
        this.hidden = false;
      } catch (error) {
        console.warn('[SHD series] Inventory refresh failed:', error);
        if (this.request !== request || !this.isConnected) return;
        this.setAttribute('data-stale', '');
        this.querySelector('[data-shd-live-label]').textContent = 'Last known stock';
      } finally { clearTimeout(timeout); }
    }
  }
  customElements.define('shd-series-counter', SeriesCounter);
})();